import { FinancialDocumentListItem } from "@common/types/financialDocument";
import {
    FinancialDocumentColumn,
    FinancialDocumentSortValue,
    FinancialDocumentView,
} from "@common/types/views/financialDocumentViews";
import { ViewDensity, ViewType } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { UploadDateCell } from "../FinancialDocumentsView/Cells/UploadDateCell";
import { NameCell } from "../FinancialDocumentsView/Cells/NameCell";
import { EntityCell } from "../FinancialDocumentsView/Cells/EntityCell";
import { MatchedTransactionCell } from "../FinancialDocumentsView/Cells/MatchedTransactionCell";
import { UploaderCell } from "../FinancialDocumentsView/Cells/UploaderCell";
import { DocumentCategoryCell } from "../FinancialDocumentsView/Cells/DocumentCategoryCell";
import { CounterpartyCell } from "../FinancialDocumentsView/Cells/CounterpartyCell";
import { PeriodCell } from "../FinancialDocumentsView/Cells/PeriodCell";
import { FinancialAccountCell } from "../FinancialDocumentsView/Cells/FinancialAccountCell";
import { DocumentStatusCell } from "../FinancialDocumentsView/Cells/DocumentStatusCell";
import { SelectActionsCell } from "./SelectActionsCell";

interface DocumentSelectCallbacks {
    cta?: (document: FinancialDocumentListItem) => React.ReactNode;
    onPreviewed?: (document: FinancialDocumentListItem) => void;
}

export type DocumentSelectTableMeta = ViewTableMeta<
    DocumentSelectCallbacks,
    unknown
>;

export const financialDocumentSelectViewConfig: ViewConfig<
    FinancialDocumentListItem,
    FinancialDocumentColumn,
    FinancialDocumentSortValue
> = {
    columns: {
        [FinancialDocumentColumn.UPLOAD_DATE]: {
            accessorKey: "createdAt",
            name: "Upload date",
            header: "Upload date",
            cell: UploadDateCell,
            sort: {
                asc: FinancialDocumentSortValue.UPLOAD_DATE_ASC,
                desc: FinancialDocumentSortValue.UPLOAD_DATE_DESC,
            },
            minSize: 120,
            maxSize: 140,
        },
        [FinancialDocumentColumn.NAME]: {
            accessorKey: "fileName",
            name: "Name",
            header: "Name",
            cell: NameCell,
            sort: {
                asc: FinancialDocumentSortValue.NAME_ASC,
                desc: FinancialDocumentSortValue.NAME_DESC,
            },
            minSize: 300,
            maxSize: 800,
        },
        [FinancialDocumentColumn.ENTITY]: {
            accessorKey: "entity",
            name: "Entity",
            header: "Entity",
            cell: EntityCell,
            minSize: 120,
            maxSize: 120,
        },
        [FinancialDocumentColumn.FINANCIAL_ACCOUNT]: {
            accessorKey: "financialAccount",
            name: "Financial account",
            header: "Account",
            cell: FinancialAccountCell,
            minSize: 140,
            maxSize: 160,
        },
        [FinancialDocumentColumn.DOCUMENT_CATEGORY]: {
            accessorKey: "taxonomyExtraction",
            name: "Document category",
            header: "Document category",
            cell: DocumentCategoryCell,
            minSize: 220,
            maxSize: 250,
        },
        [FinancialDocumentColumn.COUNTERPARTY]: {
            accessorKey: "counterparty",
            name: "Counterparty",
            header: "Counterparty",
            cell: CounterpartyCell,
            minSize: 200,
            maxSize: 300,
        },
        [FinancialDocumentColumn.PERIOD]: {
            accessorKey: "taxonomyExtraction",
            name: "Period",
            header: "Period",
            cell: PeriodCell,
            minSize: 200,
            maxSize: 300,
        },
        [FinancialDocumentColumn.MATCHED_TRANSACTION]: {
            accessorKey: "matchedTransaction",
            name: "Matched",
            header: "Matched",
            cell: MatchedTransactionCell,
            minSize: 350,
            maxSize: 350,
        },
        [FinancialDocumentColumn.UPLOADER]: {
            accessorKey: "uploader",
            name: "Uploaded by",
            header: "By",
            cell: UploaderCell,
            minSize: 50,
            maxSize: 50,
        },
        [FinancialDocumentColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            cell: DocumentStatusCell,
            minSize: 100,
            maxSize: 120,
        },
        [FinancialDocumentColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: SelectActionsCell,
            minSize: 150,
            maxSize: 150,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
};

export enum DocumentSelectVariant {
    IMPORTER = "importer",
    DOCUMENT_ATTACHMENT = "documentAttachment",
    TRANSACTION_MATCHING = "transactionMatching",
    COUNTERPARTY_ATTACHMENT = "counterpartyAttachment",
}

const baseViewProperties: Pick<
    FinancialDocumentView,
    "type" | "filters" | "options"
> = {
    type: ViewType.FINANCIAL_DOCUMENT,
    filters: {},
    options: {
        density: ViewDensity.MD,
    },
};

const actionsColumn: FinancialDocumentView["columns"][number] = {
    id: FinancialDocumentColumn.ACTIONS,
    options: { pin: "right" },
};

export const VARIANT_CACHE_KEYS: Record<
    DocumentSelectVariant,
    WORKSPACE_USER_CACHE_KEYS
> = {
    [DocumentSelectVariant.IMPORTER]:
        WORKSPACE_USER_CACHE_KEYS.FINANCIAL_DOCUMENT_SELECT_IMPORTER_VIEW_CACHE_KEY,
    [DocumentSelectVariant.DOCUMENT_ATTACHMENT]:
        WORKSPACE_USER_CACHE_KEYS.FINANCIAL_DOCUMENT_SELECT_ATTACHMENT_VIEW_CACHE_KEY,
    [DocumentSelectVariant.TRANSACTION_MATCHING]:
        WORKSPACE_USER_CACHE_KEYS.FINANCIAL_DOCUMENT_SELECT_MATCHING_VIEW_CACHE_KEY,
    [DocumentSelectVariant.COUNTERPARTY_ATTACHMENT]:
        WORKSPACE_USER_CACHE_KEYS.FINANCIAL_DOCUMENT_SELECT_COUNTERPARTY_VIEW_CACHE_KEY,
};

export const defaultDocumentSelectViews: Record<
    DocumentSelectVariant,
    FinancialDocumentView
> = {
    [DocumentSelectVariant.IMPORTER]: {
        ...baseViewProperties,
        columns: [
            { id: FinancialDocumentColumn.UPLOAD_DATE },
            { id: FinancialDocumentColumn.NAME },
            { id: FinancialDocumentColumn.FINANCIAL_ACCOUNT },
            { id: FinancialDocumentColumn.DOCUMENT_CATEGORY },
            { id: FinancialDocumentColumn.PERIOD },
            actionsColumn,
        ],
    },
    [DocumentSelectVariant.DOCUMENT_ATTACHMENT]: {
        ...baseViewProperties,
        columns: [
            { id: FinancialDocumentColumn.UPLOAD_DATE },
            { id: FinancialDocumentColumn.NAME },
            { id: FinancialDocumentColumn.DOCUMENT_CATEGORY },
            { id: FinancialDocumentColumn.PERIOD },
            actionsColumn,
        ],
    },
    [DocumentSelectVariant.TRANSACTION_MATCHING]: {
        ...baseViewProperties,
        columns: [
            { id: FinancialDocumentColumn.UPLOAD_DATE },
            { id: FinancialDocumentColumn.NAME },
            { id: FinancialDocumentColumn.DOCUMENT_CATEGORY },
            { id: FinancialDocumentColumn.PERIOD },
            { id: FinancialDocumentColumn.MATCHED_TRANSACTION },
            actionsColumn,
        ],
    },
    [DocumentSelectVariant.COUNTERPARTY_ATTACHMENT]: {
        ...baseViewProperties,
        columns: [
            { id: FinancialDocumentColumn.UPLOAD_DATE },
            { id: FinancialDocumentColumn.NAME },
            { id: FinancialDocumentColumn.DOCUMENT_CATEGORY },
            { id: FinancialDocumentColumn.PERIOD },
            actionsColumn,
        ],
    },
};
